/**
 * ขั้นที่ 1 — จัดเตรียมวัตถุดิบ (Store Staging)
 *
 * Store ย้าย input lot จากคลังไปจุด STAGING ข้างไลน์ แล้วจองให้ order/operation
 * การย้ายนี้ **เป็นการจอง ไม่ใช่ consumption** — ไม่มี ledger ไม่มีบัญชี ของถูกตัด
 * จริงตอน Scan-Out (D-063, `references/shop-floor-pull-traceability.md`)
 *
 * หน้าจอนี้ไม่ POST: ค่าที่กรอกกลายเป็นคำสั่ง `STAGING_TRANSFER` หนึ่งใบในคิว
 * ออฟไลน์ และเซิร์ฟเวอร์ยังไม่เห็นอะไรจนกว่า sync engine จะส่งให้ (D-075)
 *
 * ทุกด่านของ contract (order ต้อง RELEASED/IN_PROCESS, lot ต้อง `RELEASED`,
 * ของต้องพอ, ประเภทของ location) **ยังเป็นของเซิร์ฟเวอร์ทั้งหมด** — หน้าจอตรวจ
 * แค่ว่ากรอกครบและรูปแบบตัวเลขใช้ได้ เพื่อไม่ให้คำสั่งที่กรอกไม่ครบไปนอนรอในคิว
 */

import { useState } from 'react';

import { ScanCommandForm, ScanField, type ScanScreenProps } from './ScanCommandForm';
import { useScanSubmit, type ScanCommandDraft } from './useScanSubmit';
import { STAGING_TRANSFER } from '../lib/shopFloor/commands';
import { isPositiveDecimal, isPositiveInteger } from '../lib/shopFloor/quantities';

export function StoreStagingScreen({ pendingSync }: ScanScreenProps) {
  const [plantCode, setPlantCode] = useState('');
  const [productionOrderId, setProductionOrderId] = useState('');
  const [operationSeq, setOperationSeq] = useState('');
  const [productCode, setProductCode] = useState('');
  const [lotId, setLotId] = useState('');
  const [sourceLocationCode, setSourceLocationCode] = useState('');
  const [stagingLocationCode, setStagingLocationCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const { state, submit } = useScanSubmit();

  const buildDraft = (): ScanCommandDraft => {
    const problems: string[] = [];
    const plant = plantCode.trim();
    const order = productionOrderId.trim();
    const seq = operationSeq.trim();
    const product = productCode.trim();
    const lot = lotId.trim();
    const source = sourceLocationCode.trim();
    const staging = stagingLocationCode.trim();
    // ⚠️ ไม่ trim ค่า NUMERIC: สิ่งที่พิมพ์ต้องถึงเซิร์ฟเวอร์เหมือนเดิมทุกตัวอักษร

    if (plant === '') {
      problems.push('ต้องระบุรหัสโรงงาน — ทุกคำสั่งเป็นของโรงงานใดโรงงานหนึ่งเสมอ (D-004)');
    }
    if (order === '') {
      problems.push('ต้องระบุใบสั่งผลิตที่จะจัดเตรียมให้');
    }
    if (!isPositiveInteger(seq)) {
      problems.push('ลำดับขั้นตอน (operation_seq) ต้องเป็นจำนวนเต็มมากกว่า 0');
    }
    if (product === '') {
      problems.push('ต้องระบุรหัสสินค้าของ lot ที่ย้าย');
    }
    if (lot === '') {
      problems.push('ต้องระบุ lot ต้นทาง — การจัดเตรียมโดยไม่รู้ lot ทำให้ตามรอยย้อนกลับไม่ได้');
    }
    if (source === '') {
      problems.push('ต้องระบุที่เก็บต้นทาง (คลัง)');
    }
    if (staging === '') {
      problems.push('ต้องระบุจุด STAGING ปลายทางข้างไลน์');
    }
    if (!isPositiveDecimal(quantity)) {
      problems.push('จำนวนที่จัดเตรียมต้องเป็นตัวเลขมากกว่า 0');
    }

    if (problems.length > 0) {
      return { ok: false, problems };
    }

    return {
      ok: true,
      command: {
        command_type: STAGING_TRANSFER,
        payload: {
          plant_code: plant,
          production_order_id: order,
          operation_seq: Number(seq),
          product_code: product,
          lot_id: lot,
          source_location_code: source,
          staging_location_code: staging,
          quantity,
        },
      },
    };
  };

  const handleSubmit = async () => {
    const result = await submit(buildDraft());
    if (result?.status === 'queued') {
      /* เหลือโรงงาน/ใบสั่งผลิต/ขั้นตอนไว้ — Store มักจัดเตรียมหลาย lot ให้งานเดียวกันติดกัน */
      setProductCode('');
      setLotId('');
      setQuantity('');
    }
  };

  return (
    <ScanCommandForm
      testId="store-staging-screen"
      title="จัดเตรียมวัตถุดิบเข้าจุด STAGING"
      hint="ย้าย lot จากคลังไปข้างไลน์และจองให้ขั้นตอนนี้ — ยังไม่ตัดสต๊อก ของถูกตัดจริงตอนสแกนออก"
      submitLabel="เข้าคิวการจัดเตรียม"
      state={state}
      pendingSync={pendingSync}
      onSubmit={() => {
        void handleSubmit();
      }}
    >
      <ScanField
        label="รหัสโรงงาน (plant_code)"
        name="plant_code"
        testId="store-staging-plant"
        value={plantCode}
        onChange={setPlantCode}
        placeholder="เช่น BKK-01"
        hint="ยังต้องกรอกเองจนกว่าอุปกรณ์จะถูกลงทะเบียนผูกกับโรงงาน/ไลน์"
      />
      <ScanField
        label="ใบสั่งผลิต (production_order_id)"
        name="production_order_id"
        testId="store-staging-order"
        value={productionOrderId}
        onChange={setProductionOrderId}
      />
      <ScanField
        label="ลำดับขั้นตอน (operation_seq)"
        name="operation_seq"
        testId="store-staging-operation"
        value={operationSeq}
        onChange={setOperationSeq}
        inputMode="numeric"
        placeholder="เช่น 20"
      />
      <ScanField
        label="รหัสสินค้า (product_code)"
        name="product_code"
        testId="store-staging-product"
        value={productCode}
        onChange={setProductCode}
        placeholder="เช่น RM-STEEL-8MM"
      />
      <ScanField
        label="lot ต้นทาง (lot_id)"
        name="lot_id"
        testId="store-staging-lot"
        value={lotId}
        onChange={setLotId}
        hint="lot ที่ยังไม่ผ่านการปล่อยคุณภาพจะถูกเซิร์ฟเวอร์ปฏิเสธตอนซิงค์ (D-059)"
      />
      <ScanField
        label="ที่เก็บต้นทาง (source_location_code)"
        name="source_location_code"
        testId="store-staging-source"
        value={sourceLocationCode}
        onChange={setSourceLocationCode}
        placeholder="เช่น RM-01"
      />
      <ScanField
        label="จุด STAGING ปลายทาง (staging_location_code)"
        name="staging_location_code"
        testId="store-staging-destination"
        value={stagingLocationCode}
        onChange={setStagingLocationCode}
        placeholder="เช่น LINE-STG-01"
      />
      <ScanField
        label="จำนวนที่จัดเตรียม (quantity)"
        name="quantity"
        testId="store-staging-quantity"
        value={quantity}
        onChange={setQuantity}
        inputMode="decimal"
        placeholder="เช่น 50.0000"
        hint="ส่งเป็นข้อความตามที่พิมพ์ ไม่ปัดเศษและไม่แปลงเป็นตัวเลขระหว่างทาง"
      />
    </ScanCommandForm>
  );
}
